import api from "../../services/api";
import type {
  TournamentPlayer,
  CreateTournamentPlayerRequest,
  UpdateTournamentPlayerRequest,
  GetTournamentPlayersResponse,
  PlayerPosition,
} from "./types";

export const playersApi = {
  /**
   * Create a new tournament player
   */
  createTournamentPlayer: async (data: CreateTournamentPlayerRequest): Promise<TournamentPlayer> => {
    return api.post("/tournament-players", data);
  },

  /**
   * Get all tournament players (paginated) with search, sort, and filter support
   */
  getTournamentPlayers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: "name" | "createdAt" | "position" | "number" | "dateOfBirth" | "gender" | "clubName";
    sortOrder?: "asc" | "desc";
    tournamentId?: string;
    clubId?: string;
    userId?: string;
    gender?: "male" | "female" | "other";
    position?: PlayerPosition;
    isManual?: boolean;
    minAge?: number;
    maxAge?: number;
  }): Promise<GetTournamentPlayersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.limit) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.sortBy) {
      queryParams.append("sortBy", params.sortBy);
    }
    if (params?.sortOrder) {
      queryParams.append("sortOrder", params.sortOrder);
    }
    if (params?.tournamentId) {
      queryParams.append("tournamentId", params.tournamentId);
    }
    if (params?.clubId) {
      queryParams.append("clubId", params.clubId);
    }
    if (params?.userId) {
      queryParams.append("userId", params.userId);
    }
    if (params?.gender) {
      queryParams.append("gender", params.gender);
    }
    if (params?.position) {
      queryParams.append("position", params.position);
    }
    if (params?.isManual !== undefined) {
      queryParams.append("isManual", params.isManual.toString());
    }
    if (params?.minAge !== undefined) {
      queryParams.append("minAge", params.minAge.toString());
    }
    if (params?.maxAge !== undefined) {
      queryParams.append("maxAge", params.maxAge.toString());
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/tournament-players?${queryString}` : "/tournament-players";
    return api.get(url);
  },

  /**
   * Get players by tournament (paginated) - uses the main endpoint with tournamentId filter
   */
  getPlayersByTournament: async (
    tournamentId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sortBy?: "name" | "createdAt" | "position" | "number" | "dateOfBirth" | "gender" | "clubName";
      sortOrder?: "asc" | "desc";
      clubId?: string;
      userId?: string;
      gender?: "male" | "female" | "other";
      position?: PlayerPosition;
      isManual?: boolean;
      minAge?: number;
      maxAge?: number;
    }
  ): Promise<GetTournamentPlayersResponse> => {
    return playersApi.getTournamentPlayers({
      ...params,
      tournamentId,
    });
  },

  /**
   * Get players by user
   */
  getPlayersByUser: async (userId: string): Promise<TournamentPlayer[]> => {
    return api.get(`/tournament-players/user/${userId}`);
  },

  /**
   * Get players by club
   */
  getPlayersByClub: async (clubId: string): Promise<TournamentPlayer[]> => {
    return api.get(`/tournament-players/club/${clubId}`);
  },

  /**
   * Get players without a club
   */
  getPlayersWithoutClub: async (tournamentId?: string): Promise<TournamentPlayer[]> => {
    const queryParams = new URLSearchParams();
    if (tournamentId) {
      queryParams.append("tournamentId", tournamentId);
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/tournament-players/without-club?${queryString}` : "/tournament-players/without-club";
    return api.get(url);
  },

  /**
   * Get tournament player by ID
   */
  getTournamentPlayer: async (id: string): Promise<TournamentPlayer> => {
    return api.get(`/tournament-players/${id}`);
  },

  /**
   * Update tournament player
   */
  updateTournamentPlayer: async (id: string, data: UpdateTournamentPlayerRequest): Promise<TournamentPlayer> => {
    return api.put(`/tournament-players/${id}`, data);
  },

  /**
   * Delete tournament player
   */
  deleteTournamentPlayer: async (id: string): Promise<void> => {
    return api.delete(`/tournament-players/${id}`);
  },
};
